import { DisplacementNotice, PropertyId, Uuid } from "@townsy/contracts";
import { z } from "zod";
import { formatCents } from "../lib/format";
import { readJson, writeJson } from "../lib/storage";
import { adNoticeFromNotification, type AdNotice } from "../advertise/adNotices";

/**
 * In-app commerce notices (ADR-0007 #13): "your store was taken over" (with the exact credit
 * granted) and "your purchase is complete". The source is the server — the account's
 * notifications (`GET /me/notifications`), or, where that route does not exist, the immutable
 * Townsy Credit journal (`DISPLACEMENT_CREDIT` entries of `GET /me/wallets/CREDIT_USD/history`).
 * Nothing is inferred locally; this module only formats and de-duplicates what the server said.
 */

export const OrderFulfilledNotice = z
  .object({
    orderId: Uuid,
    receiptNumber: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    propertyId: PropertyId.nullable().optional(),
  })
  .loose();
export type OrderFulfilledNotice = z.infer<typeof OrderFulfilledNotice>;

export type CommerceNotice =
  | { key: string; kind: "displaced"; creditCents: number; address: string | null; propertyId: string | null; at: string | null; text: string }
  | { key: string; kind: "fulfilled"; orderId: string; receiptNumber: string | null; propertyId: string | null; at: string | null; text: string }
  /** M5 advertiser notices (creative review, billboard rentals, sign replaced after a takeover). */
  | AdNotice
  /** Moderators and admins: a report is waiting in Review > Reports (no content; read it there). */
  | { key: string; kind: "report"; reportId: string; at: string | null; text: string };

export const REPORT_NOTICE_TEXT = "Someone filed a report. It is waiting in Review > Reports.";

export function displacementText(creditCents: number, address: string | null): string {
  const where = address ? `your store at ${address}` : "your store";
  return `You received ${formatCents(creditCents)} Townsy Credit because ${where} was taken over. It is 100% of what you paid; Townsy Credit is not cash.`;
}

export function fulfilledText(n: Pick<OrderFulfilledNotice, "description" | "receiptNumber">): string {
  const what = n.description ? n.description : "Your purchase";
  return `${what}: purchase complete${n.receiptNumber ? ` (receipt ${n.receiptNumber})` : ""}.`;
}

/** One server notification -> a notice we can show (unknown types / invalid payloads are ignored). */
export function noticeFromNotification(item: { id: string; type: string; payload: unknown; createdAt?: string }): CommerceNotice | null {
  if (item.type === "property.displaced") {
    const p = DisplacementNotice.safeParse(item.payload);
    if (!p.success) return null;
    return {
      key: `n:${item.id}`,
      kind: "displaced",
      creditCents: p.data.creditGrantedCents,
      address: p.data.address,
      propertyId: p.data.propertyId,
      at: p.data.at,
      text: displacementText(p.data.creditGrantedCents, p.data.address),
    };
  }
  if (item.type === "order.fulfilled") {
    const p = OrderFulfilledNotice.safeParse(item.payload);
    if (!p.success) return null;
    return {
      key: `o:${p.data.orderId}`,
      kind: "fulfilled",
      orderId: p.data.orderId,
      receiptNumber: p.data.receiptNumber ?? null,
      propertyId: p.data.propertyId ?? null,
      at: item.createdAt ?? null,
      text: fulfilledText(p.data),
    };
  }
  if (item.type === "report.filed") {
    const p = z.object({ reportId: Uuid }).loose().safeParse(item.payload);
    if (!p.success) return null;
    return { key: `r:${p.data.reportId}`, kind: "report", reportId: p.data.reportId, at: item.createdAt ?? null, text: REPORT_NOTICE_TEXT };
  }
  return adNoticeFromNotification(item);
}

/**
 * Fallback source: a `DISPLACEMENT_CREDIT` journal in the credit history. The address is taken
 * from the server's own description ("Townsy Credit for your store at 12 Main Street (...)") when
 * it has that form; otherwise the notice simply says "your store".
 */
export function noticeFromCreditHistory(item: { journalId: string; type: string; amount: number; description: string; at: string }): CommerceNotice | null {
  if (item.type !== "DISPLACEMENT_CREDIT" || item.amount <= 0) return null;
  const m = /store at (.+?)(?: \(|$)/.exec(item.description);
  const address = m?.[1]?.trim() || null;
  return {
    key: `j:${item.journalId}`,
    kind: "displaced",
    creditCents: item.amount,
    address,
    propertyId: null,
    at: item.at,
    text: displacementText(item.amount, address),
  };
}

// ---------------------------------------------------------------- once per browser (per account)

const SEEN_KEY = "townsy.commerceNotices.v1";
const MAX_SEEN = 200;

interface SeenStore {
  [userId: string]: { keys: string[]; since: string };
}

/**
 * Which notices this browser already showed for an account. The first time an account is seen,
 * only notices from the last `firstVisitWindowMs` are shown (older ones are in the wallet history).
 */
export class SeenNotices {
  constructor(
    private readonly userId: string,
    private readonly now: () => number = Date.now,
    private readonly firstVisitWindowMs = 14 * 24 * 3600_000,
  ) {}

  private load(): { keys: string[]; since: string } {
    const all = readJson<SeenStore>("local", SEEN_KEY) ?? {};
    const mine = all[this.userId];
    if (mine && Array.isArray(mine.keys) && typeof mine.since === "string") return mine;
    const fresh = { keys: [], since: new Date(this.now() - this.firstVisitWindowMs).toISOString() };
    writeJson("local", SEEN_KEY, { ...all, [this.userId]: fresh });
    return fresh;
  }

  /** Notices not shown yet (and not older than the first-visit window), oldest first, at most `max`. */
  unseen(notices: CommerceNotice[], max = 3): CommerceNotice[] {
    const { keys, since } = this.load();
    const seen = new Set(keys);
    const sinceMs = Date.parse(since);
    return notices
      .filter((n) => !seen.has(n.key))
      .filter((n) => {
        if (!n.at || !Number.isFinite(sinceMs)) return true;
        const t = Date.parse(n.at);
        return !Number.isFinite(t) || t >= sinceMs;
      })
      .slice(0, max);
  }

  /** True when this browser already showed the notice for this account (read only). */
  has(key: string): boolean {
    const mine = readJson<SeenStore>("local", SEEN_KEY)?.[this.userId];
    return Boolean(mine && Array.isArray(mine.keys) && mine.keys.includes(key));
  }

  markSeen(keys: string[]): void {
    if (keys.length === 0) return;
    const all = readJson<SeenStore>("local", SEEN_KEY) ?? {};
    const mine = all[this.userId] ?? { keys: [], since: new Date(this.now()).toISOString() };
    const next = [...new Set([...mine.keys, ...keys])].slice(-MAX_SEEN);
    writeJson("local", SEEN_KEY, { ...all, [this.userId]: { ...mine, keys: next } });
  }
}
